import helperStatus from './status';
import states from './states';

export interface NormalizedAddressComponents {
  addressLine1: string;
  addressLine2: string;
  city: string;
  stateCode: string;
  postalCode: string;
  placeId: string;
}

export interface NormalizedAddress {
  components: NormalizedAddressComponents;
  formattedAddress: string;
  geoPoint: {
    x: number;
    y: number;
  } | null;
}

export const getAddress = (results: Array<any>) => {
  const findZipObject = results.find((address) => address.types[0] === 'postal_code');
  const addressComponents = findZipObject?.address_components;
  const undefOrNull = [findZipObject, addressComponents].some((item) => item === undefined || item === null);
  if (undefOrNull) {
    return helperStatus.Error;
  }
  const zipcode = addressComponents.find((addrComp) => addrComp.types.includes('postal_code'))?.short_name;
  const city = addressComponents.find((addrComp) => addrComp.types.includes('political'))?.long_name;
  const state = addressComponents.find((addrComp) => addrComp.types.includes('administrative_area_level_1'))?.short_name;
  if (zipcode && city && state) {
    return `${city}, ${state} ${zipcode}`;
  }
  return helperStatus.Error;
};

export const isValidZipcode = (zipcode: string | null | undefined): boolean => {
  if (!zipcode) {
    return false;
  }
  const zipcodeRegex = /^\d{5}(-\d{4})?$/;
  return zipcodeRegex.test(zipcode);
};

/**
 * Converts a full state name to its abbreviation.
 * Returns the input unchanged if it's already an abbreviation or not found.
 *
 * @param stateName - Full state name (e.g., "North Carolina") or abbreviation (e.g., "NC")
 * @returns State abbreviation (e.g., "NC")
 */
export const getStateAbbreviation = (stateName: string): string => {
  const stateMap = new Map(states.map((state) => [state.display.toLowerCase(), state.value]));
  return stateMap.get(stateName.toLowerCase()) || stateName;
};

/**
 * Normalizes Google Places API address data into a consistent format.
 * Uses short_name for state to ensure abbreviations are always used.
 *
 * @param placeData - Google Places API place data object
 * @returns Normalized address object with components and formatted address string, or null if invalid
 */
export const normalizeGooglePlacesAddress = (placeData: any): NormalizedAddress | null => {
  if (!placeData || !placeData.address_components) {
    return null;
  }

  const googleToDatabaseKeys: Record<string, keyof NormalizedAddressComponents> = {
    street_number: 'addressLine1',
    route: 'addressLine1',
    locality: 'city',
    administrative_area_level_1: 'stateCode',
    postal_code: 'postalCode',
  };

  const components: NormalizedAddressComponents = {
    addressLine1: '',
    addressLine2: '',
    city: '',
    stateCode: '',
    postalCode: '',
    placeId: placeData.place_id || '',
  };

  placeData.address_components.forEach((component: any) => {
    const addressType = component.types[0];
    const targetKey = googleToDatabaseKeys[addressType];

    if (targetKey && targetKey !== 'addressLine2') {
      if (targetKey === 'addressLine1') {
        components[targetKey] = `${components[targetKey]} ${component.short_name}`.trim();
      } else {
        components[targetKey] = component.short_name || '';
      }
    }
  });

  if (!components.city) {
    const sublocality = placeData.address_components.find((component: any) => component.types.includes('sublocality'));
    if (sublocality) {
      components.city = sublocality.short_name || '';
    }
  }

  if (!components.addressLine1 || !components.city || !components.stateCode || !components.postalCode) {
    return null;
  }

  const formattedAddress = `${components.addressLine1}, ${components.city}, ${components.stateCode} ${components.postalCode}`;

  let geoPoint: { x: number; y: number } | null = null;
  if (placeData.geometry?.location) {
    const { location } = placeData.geometry;
    geoPoint = {
      x: typeof location.lng === 'function' ? location.lng() : location.lng,
      y: typeof location.lat === 'function' ? location.lat() : location.lat,
    };
  }

  return {
    components,
    formattedAddress,
    geoPoint,
  };
};
